
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { setSessionExpiredCallback } from './utils/api';
import { hasPermission, permissionLabel } from './utils/permissions';
import Login from './components/Login';
import Registration from './components/Registration';
import CompanyManagement from './components/CompanyManagement';
import AdminCreation from './components/AdminCreation';
import HomePage from './pages/HomePage';
import UploadDownloadPage from './pages/UploadDownloadPage';
import NavigationBar from './components/NavigationBar';
import usePersistedRoute from './hooks/usePersistedRoute';
import Personnel from './pages/Personnel';
import CompanyInfo from './pages/CompanyInfo';
import InvestorPortal from './pages/InvestorPortal'; 
import GrantList from './pages/GrantList'; 
import GrantReport from './components/GrantReport';
import CustomizedCategories from './pages/CustomizedCategories';
import CustomizedDepositCategories from './pages/CustomizedDepositCategories';
import ExpenseTable from './components/ExpenseTable'; 
import CreditCardExpense from './components/CreditCardExpense';
import Payroll from './pages/Payroll';
import BankStatement from './pages/BankStatement';
import CreditCardCalculator from './pages/CreditCardCalculator';
import ConfirmedTransactions from './pages/ConfirmedTransactions';
import PayrollBreakdown from './pages/PayrollBreakdown';
import PayrollPeriodDetail from './pages/PayrollPeriodDetail';
import RegistrationsPage from './pages/Registrations';
import LegalDocumentsPage from './pages/LegalDocuments';
import CapTable from './pages/CapTable';
import GrantSubmission from './pages/GrantSubmission';
import GrantQuestionnaire from './pages/GrantQuestionnaire';
import GrantDocuments from './pages/GrantDocuments';
import GrantJITDocuments from './pages/GrantJITDocuments';
import GrantNoticeOfAward from './pages/GrantNoticeOfAward';
import GrantRelatedDocuments from './pages/GrantRelatedDocuments';
import SubcontractorDocumentsManagement from './pages/SubcontractorDocumentsManagement';
import BudgetForms from './pages/BudgetForms';
import SubcontractUpload from './pages/SubcontractUpload';
import GrantManagementPage from './pages/GrantManagement';
import GrantFundFinancials from './pages/GrantFundFinancials';
import PMSPayments from './pages/PMSPayments';
import DataRoom from './pages/DataRoom';
import ExecutiveSummary from './pages/ExecutiveSummary';
import PitchDeck from './pages/PitchDeck';
import PreliminaryData from './pages/PreliminaryData';
import PharmacologyToxicology from './pages/PharmacologyToxicology';
import CMC from './pages/CMC';
import Team from './pages/Team';
import EmailVerification from './pages/EmailVerification';
import UserManagement from './pages/UserManagement';
import CompanyFormation from './pages/CompanyFormation';
import FounderTodolist from './pages/FounderTodolist';
import AdminTodolist from './pages/AdminTodolist';
import TaskDocuments from './pages/TaskDocuments';
import TaskSettings from './pages/TaskSettings';
import BoardMeetingMinutes from './pages/BoardMeetingMinutes';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyUsernameChange from './components/VerifyUsernameChange';
import InvestorAccessRequest from './pages/InvestorAccessRequest';
import InvestorEmailConfirmation from './pages/InvestorEmailConfirmation';
import InvestorDashboard from './pages/InvestorDashboard';
import InvestorCompanyView from './pages/InvestorCompanyView';


function App() {
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [needsCompanyManagement, setNeedsCompanyManagement] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  const getRedirectTarget = () => {
    const params = new URLSearchParams(location.search);
    return location.state?.from || params.get('redirect') || null;
  };

  const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    if (!authReady) {
      return null;
    }

    if (!user) {
      const intendedPath = `${location.pathname}${location.search}${location.hash}`;
      return <Navigate to={`/login?redirect=${encodeURIComponent(intendedPath)}`} state={{ from: intendedPath }} replace />;
    }

    // Investor accounts don't need company selection
    if (userInfo?.accountType === 'investor') {
      return children;
    }

    if ((userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && !userInfo?.selectedCompany) {
      return <Navigate to="/company-management" replace />;
    }

    return children;
  }; 

  // Blocks a restricted user from reaching a section by URL when the matching
  // permission checkbox isn't set for them.
  const PermissionRoute = ({ permission, children }) => {
    if (!authReady) return null;
    if (userInfo && !hasPermission(userInfo, permission)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
          <div className="card p-6 max-w-md mx-auto text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h2>
            <p className="text-gray-600">
              You don't have access to {permissionLabel(permission)}. Contact a Founder or
              Super User at your company if you need it.
            </p>
          </div>
        </div>
      );
    }
    return children;
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedUserInfo = localStorage.getItem('userInfo');

    // If there's nothing stored, we're done — user is logged out.
    if (!storedUser) {
      setAuthReady(true);
      return;
    }

    // We have a stored user, but the server session (cookie) may have expired
    // while the app was closed. Validate it before trusting localStorage so a
    // dead session lands on the login page instead of restoring a broken route.
    const validateSession = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/check`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Session invalid');
        }

        // Session is still valid — hydrate state from localStorage.
        setUser(storedUser);
        if (storedUserInfo) {
          const stored = JSON.parse(storedUserInfo);
          // Role and permissions can be changed by a Founder / Super User while
          // this session is open, so always take the server's latest values.
          const fresh = await response.json().catch(() => ({}));
          const userInfoData = {
            ...stored,
            ...(fresh.companyRole !== undefined ? { companyRole: fresh.companyRole } : {}),
            ...(Array.isArray(fresh.permissions) ? { permissions: fresh.permissions } : {})
          };
          setUserInfo(userInfoData);
          localStorage.setItem('userInfo', JSON.stringify(userInfoData));
          if (userInfoData.accountType === 'admin' && userInfoData.selectedCompany) {
            setNeedsCompanyManagement(false);
          }
        }
      } catch (error) {
        // Session expired/invalid: clear all persisted auth state and the last
        // route so the user is sent to the login page on reopen.
        localStorage.removeItem('user');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('authToken');
        localStorage.removeItem('lastRoute');
        setUser(null);
        setUserInfo(null);
        setNeedsCompanyManagement(false);
      } finally {
        setAuthReady(true);
      }
    };

    validateSession();
  }, []);

  usePersistedRoute();

  // Watch for changes to userInfo to update needsCompanyManagement
  useEffect(() => {
    if (userInfo?.accountType === 'admin' && userInfo?.selectedCompany) {
      setNeedsCompanyManagement(false);
    }
  }, [userInfo]);

  // Set up session expiry callback
  useEffect(() => {
    const handleSessionExpiry = () => {
      handleLogout(true); // Pass true to indicate session expiry
    };

    setSessionExpiredCallback(handleSessionExpiry);

    // Cleanup: remove callback when component unmounts
    return () => {
      setSessionExpiredCallback(null);
    };
  }, [navigate]); // Include navigate in dependencies since handleLogout uses it

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    let idleTimer = null;

    const resetIdleTimer = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        alert('You have been logged out due to 30 minutes of inactivity.');
        handleLogout(true);
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      activityEvents.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
    };
  }, [user]);

  const handleResponse = async (response) => {
    console.log("hi");
    const data = await response.text();
    try {
      const jsonData = JSON.parse(data);
      if (!response.ok) {
        const errorMessage = jsonData.error || jsonData.message || 'Request failed';
        console.error(`HTTP error ${response.status}: ${errorMessage}`);
        console.log(jsonData);
        throw new Error(errorMessage);
      }
      return jsonData;
    } catch (error) {
      console.log("erro");
      if (!response.ok) {
        console.log("error:", error)
        throw new Error(error.message);
      }
      return data;
    }
  };

  const handleLogin = async (username, password, selectedCompany = null) => {
    setError(''); // Clear any existing errors
    setSuccessMessage(''); // Clear any success messages
    console.log("logging");
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password, selectedCompany }),
      });
      const data = await handleResponse(response);

      console.log('Login response:', data);
      
      if (data.needsCompanyManagement) {
        // Admin user needs to manage companies
        setNeedsCompanyManagement(true);
        setUser(data.username);
        setUserInfo(data);
        localStorage.setItem('user', data.username);
        localStorage.setItem('userInfo', JSON.stringify(data));
        if (data.authToken) {
          localStorage.setItem('authToken', data.authToken);
        }
        navigate('/company-management', { replace: true });
      } else {
        // User is ready to proceed (client account)
        console.log("naving to home");
        setUser(data.username);
        setUserInfo(data);
        setNeedsCompanyManagement(false);
        localStorage.setItem('user', data.username);
        localStorage.setItem('userInfo', JSON.stringify(data));
        if (data.authToken) {
          localStorage.setItem('authToken', data.authToken);
        }
        const redirectTo = getRedirectTarget();
        // Redirect investor accounts to investor dashboard
        if (data.accountType === 'investor') {
          navigate(redirectTo || '/investor-dashboard', { replace: true });
        } else {
          // Redirect to the page the user was trying to access, or home
          navigate(redirectTo || '/home', { replace: true });
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message || 'Login failed. Please check your credentials and try again.');
    }
  };


  const handleLogout = async (isSessionExpired = false) => {
    try {
      // Only call logout endpoint if this is not a session expiry
      if (!isSessionExpired) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        });
        await handleResponse(response);
      }

      // Clear local storage and state
      localStorage.removeItem('user');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('authToken');
      setUser(null);
      setUserInfo(null);
      setNeedsCompanyManagement(false);

      navigate('/login');

      // Clear any error messages after navigation to avoid showing on login page
      setTimeout(() => {
        if (isSessionExpired) {
          setError('');
        }
      }, 100);
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state for session expiry
      if (isSessionExpired) {
        localStorage.removeItem('user');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('authToken');
        setUser(null);
        setUserInfo(null);
        setNeedsCompanyManagement(false);
        navigate('/login');

        // Clear any error messages after navigation to avoid showing on login page
        setTimeout(() => {
          setError('');
        }, 100);
      } else {
        alert('Logout failed!');
      }
    }
  };

  const handleRegister = async (registrationData) => {
    // Clear previous messages before starting
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });
      
      // Handle response directly without using handleResponse to avoid double error handling
      const data = await response.json();
      console.log("handling register", data);
      
      if (response.ok && data.success) { 
        // Use the backend message which correctly indicates whether email verification is needed
        setSuccessMessage(data.message || 'Registration successful! Please check your email for verification.');
      } else {
        const errorMessage = data.message || data.error || 'Registration failed. Please try again.';
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Registration failed:', error);
      setError(error.message || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="App min-h-screen bg-gray-50">
      {user && !needsCompanyManagement && (
        <NavigationBar 
          onLogout={handleLogout} 
          userInfo={userInfo}
          onBackToCompanyManagement={() => {
            setNeedsCompanyManagement(true);
            navigate('/company-management');
          }}
        />
      )}
      <Routes>
        <Route path="/" element={
          !authReady ? null : user ? (
            userInfo?.accountType === 'investor' ? <Navigate to="/investor-dashboard" /> :
            needsCompanyManagement ? <Navigate to="/company-management" /> : <Navigate to="/home" />
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route
          path="/login"
          element={
            !authReady ? null : user ? (
              <Navigate
                to={
                  getRedirectTarget() ||
                  (userInfo?.accountType === 'investor'
                    ? '/investor-dashboard'
                    : needsCompanyManagement
                      ? '/company-management'
                      : '/home')
                }
                replace
              />
            ) : (
              <Login 
                onLogin={handleLogin} 
                onNavigateToForgotPassword={() => {
                  clearMessages();
                  navigate('/forgot-password');
                }}
                errorMessage={error} 
              />
            )
          }
        />
        <Route path="/register" element={user ? <Navigate to="/home" /> : <Registration onRegister={handleRegister} errorMessage={error} successMessage={successMessage} onClearMessages={clearMessages} onNavigateToLogin={() => {
                clearMessages();
                navigate('/login');
              }} />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/verify-username-change/:token" element={<VerifyUsernameChange />} />
        <Route path="/subcontract-upload/:token" element={<SubcontractUpload />} />
        <Route path="/investor-request" element={<InvestorAccessRequest />} />
        <Route path="/investor-confirm-email" element={<InvestorEmailConfirmation />} />
        <Route path="/forgot-password" element={
          user ? <Navigate to="/home" /> : 
          <ForgotPassword onNavigateToLogin={() => {
            clearMessages();
            navigate('/login');
          }} />
        } />
        <Route path="/reset-password" element={
          user ? <Navigate to="/home" /> : 
          <ResetPassword onNavigateToLogin={() => {
            clearMessages();
            navigate('/login');
          }} />
        } />
        <Route path="/home" element={
          <ProtectedRoute>
            <HomePage user={user} userInfo={userInfo} />
          </ProtectedRoute>
        } />
        <Route path="/upload-download" element={<ProtectedRoute><UploadDownloadPage /></ProtectedRoute>} />
        <Route path="/legal-documents" element={<ProtectedRoute><PermissionRoute permission="legal_documents"><LegalDocumentsPage /></PermissionRoute></ProtectedRoute>} />
        <Route path="/registrations" element={<ProtectedRoute><PermissionRoute permission="registrations"><RegistrationsPage /></PermissionRoute></ProtectedRoute>} />
        <Route path="/cap-table" element={<ProtectedRoute><PermissionRoute permission="cap_table"><CapTable userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/personnel" element={<ProtectedRoute><PermissionRoute permission="personnel"><Personnel /></PermissionRoute></ProtectedRoute>} />
        <Route path="/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
        <Route path="/payroll-breakdown" element={<ProtectedRoute><PayrollBreakdown /></ProtectedRoute>} />
        <Route path="/payroll/period/:period" element={<ProtectedRoute><PayrollPeriodDetail /></ProtectedRoute>} />
        <Route path="/bank-statement" element={<ProtectedRoute><BankStatement /></ProtectedRoute>} />
        <Route path="/confirmed-transactions" element={<ProtectedRoute><ConfirmedTransactions /></ProtectedRoute>} />
        <Route path="/company-info" element={<ProtectedRoute><PermissionRoute permission="company_info"><CompanyInfo userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-list" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantList userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-report" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantReport /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-submission" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantSubmission userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-submission/questionnaire/:applicationId" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantQuestionnaire userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-submission/documents/:applicationId" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantDocuments userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-submission/jit-documents/:applicationId" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantJITDocuments userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-submission/noa-documents/:applicationId" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantNoticeOfAward userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-related-documents" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantRelatedDocuments /></PermissionRoute></ProtectedRoute>} />
        <Route path="/subcontractor-documents/:applicationId" element={<ProtectedRoute><PermissionRoute permission="grants"><SubcontractorDocumentsManagement /></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-submission/budget/:applicationId" element={<ProtectedRoute><PermissionRoute permission="grants"><BudgetForms /></PermissionRoute></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><ExpenseTable /></ProtectedRoute>} />
        <Route path="/customized-categories" element={<ProtectedRoute><CustomizedCategories /></ProtectedRoute>} />
        <Route path="/customized-deposit-categories" element={<ProtectedRoute><CustomizedDepositCategories /></ProtectedRoute>} />
        {/* Redirect old category pages to unified pages */}
        <Route path="/customized-savings-categories" element={<Navigate to="/customized-categories" />} />
        <Route path="/customized-cc-expense-categories" element={<Navigate to="/customized-categories" />} />
        <Route path="/customized-cc-deposit-categories" element={<Navigate to="/customized-deposit-categories" />} />
        <Route path="/credit-card" element={<ProtectedRoute><CreditCardExpense /></ProtectedRoute>} />
        <Route path="/calculator" element={<ProtectedRoute><CreditCardCalculator /></ProtectedRoute>} />
        <Route path="/todolist" element={
          user ? (
            needsCompanyManagement ? <Navigate to="/company-management" /> : <Navigate to="/home" />
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route path="/grant-management" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantManagementPage user={user}/></PermissionRoute></ProtectedRoute>} />
        <Route path="/grant-fund-financials" element={<ProtectedRoute><PermissionRoute permission="grants"><GrantFundFinancials userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/pms-payments" element={<ProtectedRoute><PMSPayments /></ProtectedRoute>} />
        <Route path="/data-room" element={<ProtectedRoute><PermissionRoute permission="data_room"><DataRoom userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/data-room/executive-summary" element={<ProtectedRoute><PermissionRoute permission="data_room"><ExecutiveSummary /></PermissionRoute></ProtectedRoute>} />
        <Route path="/data-room/team" element={<ProtectedRoute><PermissionRoute permission="data_room"><Team /></PermissionRoute></ProtectedRoute>} />
        <Route path="/data-room/pitch-deck" element={<ProtectedRoute><PermissionRoute permission="data_room"><PitchDeck /></PermissionRoute></ProtectedRoute>} />
        <Route path="/data-room/preliminary-data" element={<ProtectedRoute><PermissionRoute permission="data_room"><PreliminaryData /></PermissionRoute></ProtectedRoute>} />
        <Route path="/data-room/pharmacology-toxicology" element={<ProtectedRoute><PermissionRoute permission="data_room"><PharmacologyToxicology /></PermissionRoute></ProtectedRoute>} />
        <Route path="/data-room/cmc" element={<ProtectedRoute><PermissionRoute permission="data_room"><CMC /></PermissionRoute></ProtectedRoute>} />
        <Route path="/company-formation" element={<ProtectedRoute><CompanyFormation userInfo={userInfo} /></ProtectedRoute>} />
        <Route path="/client-todolist" element={<ProtectedRoute><FounderTodolist userInfo={userInfo} /></ProtectedRoute>} />
        <Route path="/admin/todolist" element={<ProtectedRoute><AdminTodolist userInfo={userInfo} /></ProtectedRoute>} />
        <Route path="/task-documents" element={<ProtectedRoute><TaskDocuments /></ProtectedRoute>} />
        <Route path="/task-settings" element={<ProtectedRoute><TaskSettings userInfo={userInfo} /></ProtectedRoute>} />
        <Route path="/board-meeting-minutes" element={<ProtectedRoute><PermissionRoute permission="board_minutes"><BoardMeetingMinutes userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/user-management" element={<ProtectedRoute><UserManagement userInfo={userInfo} /></ProtectedRoute>} />
        <Route path="/investor-portal" element={<ProtectedRoute><PermissionRoute permission="data_room"><DataRoom userInfo={userInfo} /></PermissionRoute></ProtectedRoute>} />
        <Route path="/investor-dashboard" element={<ProtectedRoute><InvestorDashboard /></ProtectedRoute>} />
        <Route path="/investor-portal/:companyName" element={<ProtectedRoute><InvestorCompanyView /></ProtectedRoute>} />
        <Route path="/company-management" element={
          !authReady ? null : user ? (
            <CompanyManagement 
              userInfo={userInfo} 
              onLogout={handleLogout}
              onCompanySelected={(selectedCompany) => {
                const updatedUserInfo = { ...userInfo, selectedCompany };
                setUserInfo(updatedUserInfo);
                localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
                setNeedsCompanyManagement(false);
                navigate('/home');
              }}
              // The Task Management tab switches the working company in place, so
              // the app needs to agree about which company is selected without
              // leaving the admin dashboard.
              onCompanyContextChanged={(selectedCompany) => {
                const updatedUserInfo = { ...userInfo, selectedCompany };
                setUserInfo(updatedUserInfo);
                localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
              }}
            />
          ) : (
            <Navigate to="/login" />
          )
        } />
        <Route path="/create-admin" element={
          user && userInfo?.accountType === 'superadmin' ? (
            <AdminCreation userInfo={userInfo} onLogout={handleLogout} />
          ) : (
            <Navigate to="/login" />
          )
        } />
      </Routes>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}